import { type V1Exception } from '@/generated/hooks/astral/models';
import type {
  V1CompletedRunStateOutputs,
  V1Run,
} from '@/generated/hooks/bishop/models';
import { parseResourceName } from '@/shared/utils/api-utils';

/**
 * Stable display state (completed/failed/stopped). Use for display-time
 * logic. `stopped` is resumable — for state-mutation guards use
 * {@link isRunInFinalState}.
 */
export function isRunInTerminalState(state: V1Run['state']): boolean {
  if (!state) return false;
  return Boolean(state.completed || state.stopped || state.failed);
}

/**
 * Truly final state (completed/failed). Excludes `stopped` because
 * ContinueRun can transition it back to `executing`.
 */
export function isRunInFinalState(state: V1Run['state']): boolean {
  if (!state) return false;
  return Boolean(state.completed || state.failed);
}

/**
 * Total executable steps for a run, or 0 when unknown. Only present on the
 * initial `GetRun`; streamed `RunUpdate`s strip `executable_steps`.
 */
export function getRunTotalSteps(
  run: Pick<V1Run, 'executable_steps'> | null | undefined,
): number {
  return run?.executable_steps?.executables?.length ?? 0;
}

/**
 * Whether a `completed` state is an intermediate step boundary (an earlier step
 * of a multi-step run finishing) rather than the run itself, detected via
 * `active_step_index` vs `totalSteps`. False for single-step runs or when
 * `active_step_index` is absent, so completion stays terminal in the common case.
 */
export function isIntermediateStepCompletion(
  run: Pick<V1Run, 'state' | 'active_step_index'> | null | undefined,
  totalSteps: number,
): boolean {
  if (!run?.state?.completed) return false;
  if (totalSteps <= 1) return false;
  const activeStepIndex = run.active_step_index;
  if (activeStepIndex === undefined || activeStepIndex === null) return false;
  return activeStepIndex < totalSteps - 1;
}

/**
 * Completed/failed AND not an intermediate step boundary. Use instead of
 * {@link isRunInFinalState} wherever finality gates run-state progression, so an
 * intermediate `completed` can't prematurely lock the UI into a finished view.
 */
export function isRunEffectivelyFinal(
  run: Pick<V1Run, 'state' | 'active_step_index'> | null | undefined,
  totalSteps: number,
): boolean {
  if (!run) return false;
  return (
    isRunInFinalState(run.state) &&
    !isIntermediateStepCompletion(run, totalSteps)
  );
}

/**
 * Returns the canonical timestamp used to order run-state updates.
 *
 * Mirrors bishop's `run_update_ts` (bishop-utils/src/timestamp.rs), which is the
 * value bishop assigns to the SSE `Last-Event-ID` and the InfluxDB row key:
 * prefer `state.update_time` (the state-transition time set by Jeeves), falling
 * back to the top-level `update_time` only when the state-level field is absent.
 *
 * The top-level `update_time` is NOT reliably advanced on state transitions
 * (Jeeves freezes it at `create_time`), so it must never be used on its own to
 * compare recency — doing so lets a stale `pending`/`executing` update overwrite
 * a `completed` state.
 *
 * @param run - The run to read the timestamp from
 * @returns The RFC3339 timestamp string, or undefined if neither field is set
 */
export function getRunCanonicalTimestamp(
  run:
    | {
        state?: { update_time?: string } | null;
        update_time?: string;
      }
    | null
    | undefined,
): string | undefined {
  return run?.state?.update_time ?? run?.update_time;
}

/**
 * Run header control action enumeration.
 */
export enum ERunHeaderControlAction {
  PAUSE = 'pause',
  CONTINUE = 'continue',
}

/**
 * Header action type for run control button.
 */
export type TRunHeaderControlAction = ERunHeaderControlAction | null;

/**
 * Determines which run control action should be shown on a surface that
 * displays a live run — the run page header and the editor's test-run banner.
 *
 * - `CONTINUE` for stopped runs
 * - `PAUSE` for runs executing, or already unwinding from a pause
 * - `null` for everything else
 *
 * Pause is whitelisted rather than used as the fallback, because only a run
 * with work in flight can be paused. Every other state either has nothing to
 * stop yet (`pending`, `awaiting_guidance`) or is over (`completed`,
 * `failed`, `archived`) — offering Pause there produced a state transition
 * with no observable effect. `awaiting_guidance` was the worst of them: it
 * also dropped the exception off the run state on the way through
 * `stopping`/`stopped`, the case {@link pickStableActiveExceptionId} has to
 * paper over to keep the Run Assistant addressable.
 *
 * `stopping` still returns PAUSE so the control stays mounted through the
 * unwind, where surfaces render it as an in-progress "Pausing…".
 *
 * @param state - The run state object from V1Run
 * @returns The run header control action
 */
export function getRunHeaderControlAction(
  state: V1Run['state'],
): TRunHeaderControlAction {
  if (!state || state.completed || state.failed || state.archived) {
    return null;
  }

  // Checked before the rest so a stopped run stays resumable even if a stale
  // guidance field is still set alongside it.
  if (state.stopped) {
    return ERunHeaderControlAction.CONTINUE;
  }

  if (state.executing || state.stopping) {
    return ERunHeaderControlAction.PAUSE;
  }

  return null;
}

/**
 * Extracts the active exception ID from the run state.
 *
 * The active exception is the one currently in awaiting_guidance state,
 * meaning the run is paused waiting for user input.
 *
 * @param state - The run state object from V1Run
 * @returns The exception ID if one is active, null otherwise
 */
export function getActiveExceptionIdFromState(
  state: V1Run['state'],
): string | null {
  const exceptionResourceName = state?.awaiting_guidance?.exception;
  if (!exceptionResourceName) return null;

  const parsed = parseResourceName(exceptionResourceName);
  return parsed.exception ?? null;
}

/**
 * Picks the active exception ID, preserving the previous value while a run
 * is paused.
 *
 * When the user pauses a run that is in `awaiting_guidance`, the state
 * transitions through `stopping` → `stopped`. Neither carries the exception
 * field, so naively reading `awaiting_guidance.exception` would null out the
 * active exception and blank the Run Assistant card. Reusing the previously
 * known exception ID keeps the assistant addressable until the run resumes
 * (a new `awaiting_guidance`) or moves past the exception entirely.
 *
 * @param state - The current run state
 * @param lastKnownId - The most recent active exception ID, or null
 * @returns The exception ID to treat as active, or null
 */
export function pickStableActiveExceptionId(
  state: V1Run['state'],
  lastKnownId: string | null,
): string | null {
  const fromState = getActiveExceptionIdFromState(state);
  if (fromState) return fromState;
  if (state?.stopping || state?.stopped) return lastKnownId;
  return null;
}

/**
 * Combines exception IDs from events with the active exception ID.
 *
 * Merges both sources of exception IDs into a unique array,
 * ensuring no duplicates.
 *
 * @param idsFromEvents - Exception IDs extracted from run events
 * @param activeId - The currently active exception ID (may be null)
 * @returns Array of unique exception IDs
 */
export function combineExceptionIds(
  idsFromEvents: string[],
  activeId: string | null,
): string[] {
  const ids = new Set<string>(idsFromEvents);
  if (activeId) {
    ids.add(activeId);
  }
  return Array.from(ids);
}

/**
 * Calculates executed length for the code highlight. Final runs snap to the
 * full code length; everything else (including paused/`stopped`) uses
 * `executedLengthFromEvents` so the highlight reflects real progress.
 *
 * @param state - The run state object from V1Run
 * @param spyCodeLength - The total length of the automation code
 * @param executedLengthFromEvents - The max executed length calculated from events
 * @returns The current executed length
 */
export function calculateExecutedLength(
  state: V1Run['state'],
  spyCodeLength: number,
  executedLengthFromEvents: number,
): number {
  if (!state || !spyCodeLength) return 0;
  if (isRunInFinalState(state)) return spyCodeLength;
  if (executedLengthFromEvents > 0) return executedLengthFromEvents;
  return 0;
}

/**
 * Calculates the progress percentage based on executed length.
 *
 * Progress is calculated as (executedLength + 1) / spyCodeLength * 100,
 * capped at 100%.
 *
 * @param executedLength - The current executed length
 * @param spyCodeLength - The total length of the automation code
 * @returns Progress percentage (0-100)
 */
export function calculateProgress(
  executedLength: number,
  spyCodeLength: number,
): number {
  if (!spyCodeLength || spyCodeLength <= 0) return 0;
  return Math.min(
    Math.round(((executedLength + 1) / spyCodeLength) * 100),
    100,
  );
}

/**
 * Calculates progress for step-based runs (Quill V2), where each numbered step
 * in the AOP (`english_code` / manifest) is an equal slice of 100% — e.g. 4
 * steps means each completed step advances progress by 25%.
 *
 * @param completedSteps - Number of steps completed so far
 * @param totalSteps - Total number of steps in the automation (AOP)
 * @returns Progress percentage (0-100); 0 when there are no steps
 */
export function calculateStepProgress(
  completedSteps: number,
  totalSteps: number,
): number {
  if (!totalSteps || totalSteps <= 0) return 0;
  const clamped = Math.max(0, Math.min(completedSteps, totalSteps));
  return Math.round((clamped / totalSteps) * 100);
}

/**
 * Ensures progress never decreases by returning the maximum of previous and current values.
 *
 * This handles the stale closure issue where pageToken changes can cause
 * events to be replaced with a batch that has a lower max executed length.
 *
 * @param previousLength - The previous executed length
 * @param currentLength - The newly calculated executed length
 * @returns The maximum of the two values
 */
export function ensureProgressNeverDecreases(
  previousLength: number,
  currentLength: number,
): number {
  return Math.max(previousLength, currentLength);
}

/**
 * Gets the active exception if it should be shown to the user.
 *
 * Returns null if:
 * - No active exception ID
 * - Exception not found in map
 * - Run has outputs AND is not currently awaiting guidance (run finished)
 *
 * @param activeExceptionId - The ID of the currently active exception
 * @param exceptions - Map of exception IDs to exception data
 * @param outputs - The run's completed outputs (if any)
 * @param isAwaitingGuidance - Whether the run state is *currently* awaiting
 *   guidance (i.e. `state.awaiting_guidance` is set live, not a stale latch).
 *   When true, the active exception is shown even if outputs are present.
 * @returns The active exception to show, or null
 */
export function getActiveExceptionToShow(
  activeExceptionId: string | null,
  exceptions: Map<string, V1Exception>,
  outputs: V1CompletedRunStateOutputs | null,
  isAwaitingGuidance = false,
): V1Exception | null {
  if (!activeExceptionId) return null;

  const exception = exceptions.get(activeExceptionId);
  if (!exception) return null;

  // Run state (not exception.state) is the authority: an exception may already
  // read RESOLVED before the run acts on it. Treat outputs as "run finished"
  // only when not currently awaiting guidance — a run can hold outputs from an
  // earlier step while re-entering awaiting_guidance on a new fault, and that
  // pending exception must still show.
  if (outputs && !isAwaitingGuidance) {
    return null;
  }

  return exception;
}
